/**
 * HUG - Record Label Management
 * Copyright (c) 2025 Underplay Music Ltd. All rights reserved.
 */

import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { Card } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '../components/ui/dialog';
import { 
  Plus, TrendingUp, TrendingDown, Trash2, Music, PoundSterling, 
  ArrowUpCircle, ArrowDownCircle, FileText, Download, Pencil, Settings,
  ExternalLink, Banknote, Import
} from 'lucide-react';
import axios from 'axios';
import { toast } from 'sonner';
import DeleteConfirmDialog from '../components/DeleteConfirmDialog';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const RecordLabel = ({ user, selectedArtist }) => {
  const navigate = useNavigate();
  const [transactions, setTransactions] = useState([]);
  const [summaries, setSummaries] = useState([]);
  const [artistSummary, setArtistSummary] = useState(null);
  const [labelArtists, setLabelArtists] = useState([]);
  const [loading, setLoading] = useState(true);
  const [addDialogOpen, setAddDialogOpen] = useState(false);
  const [editDialogOpen, setEditDialogOpen] = useState(false);
  const [editingTransaction, setEditingTransaction] = useState(null);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [transactionToDelete, setTransactionToDelete] = useState(null);
  const [transactionType, setTransactionType] = useState('cost');
  const [selectedLabelArtist, setSelectedLabelArtist] = useState('all');
  
  // Helper to check manager-level access
  const isManager = user?.role === 'manager' || user?.role === 'admin';
  
  const [formData, setFormData] = useState({
    artist_id: '',
    amount: '',
    description: '',
    category: '',
    date: new Date().toISOString().split('T')[0],
    notes: ''
  });

  // Common categories
  const costCategories = [
    'Artist Advance',
    'Recording', 
    'Mixing', 
    'Mastering', 
    'Marketing',
    'Club/DJ',
    'Influencer Marketing Spend',
    'Misc Paid Media Spend',
    'Radio Plugger Spend',
    'Video Short Form',
    'Video Long Form',
    'TV Spend',
    'Other'
  ];
  
  const incomeCategories = [
    'Streaming', 'Downloads', 'Sync Licensing', 'Physical Sales',
    'Merchandise', 'Advances', 'Other'
  ];
  
  const drawdownCategories = [
    'Virgin/Universal Drawdown', 'Advance', 'Recoupment', 'Other'
  ];
  
  // View mode: 'itemized' or 'category'
  const [viewMode, setViewMode] = useState('itemized');
  
  // Transaction type filter
  const [transactionTypeFilter, setTransactionTypeFilter] = useState('all');
  
  // Deal settings modal
  const [showDealSettings, setShowDealSettings] = useState(false);
  const [selectedArtistForSettings, setSelectedArtistForSettings] = useState(null);
  const [dealSettings, setDealSettings] = useState({
    distributor_fee_percentage: 25,
    underplay_paythrough_percentage: 30,
    artist_split_percentage: 50,
    cost_recoupment_model: 'costs_off_top',
    term_years: '',
    term_start_date: ''
  });
  
  // Royalty participants
  const [royaltyParticipants, setRoyaltyParticipants] = useState([]);
  const [showParticipantDialog, setShowParticipantDialog] = useState(false);
  const [participantForm, setParticipantForm] = useState({
    name: '',
    role: 'Producer',
    percentage: '',
    advance_paid: '',
    notes: ''
  });
  const [editingParticipant, setEditingParticipant] = useState(null);
  
  // Virgin royalties data from DSR uploads
  const [virginRoyalties, setVirginRoyalties] = useState(null);
  const [artistRoyalties, setArtistRoyalties] = useState(null);
  const [payoutDialogOpen, setPayoutDialogOpen] = useState(false);
  const [payoutForm, setPayoutForm] = useState({
    amount: '',
    notes: '',
    date: new Date().toISOString().split('T')[0]
  });
  const [importRoyaltyDialogOpen, setImportRoyaltyDialogOpen] = useState(false);
  
  const participantRoles = ['Producer', 'Mixer', 'Engineer', 'Writer', 'Featured Artist', 'Other'];
  
  // Currency conversion
  const [displayCurrency, setDisplayCurrency] = useState('GBP');
  const [exchangeRates, setExchangeRates] = useState({
    GBP: 1,
    USD: 1.27,
    EUR: 1.17,
    AUD: 1.95,
    CAD: 1.72
  });
  
  // Calculate category totals
  const getCategoryTotals = () => {
    const totals = {};
    
    transactions.forEach(t => {
      const category = t.category || 'Other';
      if (!totals[category]) {
        totals[category] = { costs: 0, income: 0, count: 0 };
      }
      if (t.transaction_type === 'cost') {
        totals[category].costs += Math.abs(t.amount);
        totals[category].count += 1;
      } else {
        totals[category].income += t.amount;
        totals[category].count += 1;
      }
    });
    
    // Sort by total spend (costs) descending
    return Object.entries(totals)
      .sort((a, b) => b[1].costs - a[1].costs)
      .map(([category, data]) => ({
        category,
        ...data,
        net: data.income - data.costs
      }));
  };
  
  const currencies = [
    { code: 'GBP', symbol: '£', name: 'British Pound' },
    { code: 'USD', symbol: '$', name: 'US Dollar' },
    { code: 'EUR', symbol: '€', name: 'Euro' },
    { code: 'AUD', symbol: 'A$', name: 'Australian Dollar' },
    { code: 'CAD', symbol: 'C$', name: 'Canadian Dollar' }
  ];
  
  // Fetch exchange rates - only run once on mount
  const fetchExchangeRates = useCallback(async () => {
    try {
      const token = localStorage.getItem('token');
      // Fetch rates for common currencies
      const ratesPromises = ['USD', 'EUR', 'AUD', 'CAD'].map(currency =>
        axios.get(`${API}/exchange-rate/GBP/${currency}`, {
          headers: { Authorization: `Bearer ${token}` }
        }).catch(() => null)
      );
      
      const responses = await Promise.all(ratesPromises);
      const newRates = { GBP: 1 };
      const defaultRates = { USD: 1.27, EUR: 1.17, AUD: 1.93, CAD: 1.72 };
      ['USD', 'EUR', 'AUD', 'CAD'].forEach((currency, idx) => {
        newRates[currency] = responses[idx]?.data?.rate || defaultRates[currency];
      });
      setExchangeRates(newRates);
    } catch (error) {
      console.error('Failed to fetch exchange rates, using defaults:', error.message);
      // Keep default rates if fetch fails - not critical for functionality
    }
  }, []); // Empty dependency array - only create once
  
  // Convert amount from GBP to target currency
  const convertAmount = (amountGBP, toCurrency = displayCurrency) => {
    if (toCurrency === 'GBP') return amountGBP;
    const rate = exchangeRates[toCurrency] || 1;
    return amountGBP * rate;
  };
  
  // Get currency symbol
  const getCurrencySymbol = (currencyCode) => {
    const currency = currencies.find(c => c.code === currencyCode);
    return currency?.symbol || '£';
  };

  const fetchData = useCallback(async () => {
    setLoading(true);
    try {
      const token = localStorage.getItem('token');
      const headers = { Authorization: `Bearer ${token}` };

      // Fetch label artists
      const artistsRes = await axios.get(`${API}/label/artists`, { headers });
      setLabelArtists(artistsRes.data);

      // Determine which artist to filter by - use local filter first, then global
      const filterArtist = selectedLabelArtist !== 'all' 
        ? selectedLabelArtist 
        : (user?.role === 'artist' ? user.id : selectedArtist);

      // Fetch transactions
      const transactionsUrl = filterArtist && filterArtist !== 'all' && filterArtist !== 'management'
        ? `${API}/label/transactions?artist_id=${filterArtist}`
        : `${API}/label/transactions`;
      const transactionsRes = await axios.get(transactionsUrl, { headers });
      setTransactions(transactionsRes.data);

      // Fetch summaries
      if (isManager) {
        const summariesRes = await axios.get(`${API}/label/summary`, { headers });
        setSummaries(summariesRes.data);
      }

      // If specific artist selected or user is artist, get their summary
      if (filterArtist && filterArtist !== 'all' && filterArtist !== 'management') {
        try {
          const summaryRes = await axios.get(`${API}/label/summary/${filterArtist}`, { headers });
          setArtistSummary(summaryRes.data);
        } catch (e) {
          setArtistSummary(null);
        }
      } else {
        setArtistSummary(null);
      }

      // Fetch Virgin royalties data (DSR uploads)
      if (isManager) {
        try {
          const royaltiesRes = await axios.get(`${API}/royalties/dashboard-stats`, { headers });
          setVirginRoyalties(royaltiesRes.data);
        } catch (e) {
          console.error('Failed to fetch Virgin royalties:', e.message);
          setVirginRoyalties(null);
        }
        
        // Fetch artist-specific royalties if an artist is selected
        if (filterArtist && filterArtist !== 'all' && filterArtist !== 'management') {
          try {
            // Get artist name from labelArtists
            const selectedArtistObj = artistsRes.data.find(a => a.id === filterArtist);
            if (selectedArtistObj) {
              const artistRoyaltiesRes = await axios.get(
                `${API}/royalties/artists/${encodeURIComponent(selectedArtistObj.name)}`, 
                { headers }
              );
              setArtistRoyalties(artistRoyaltiesRes.data);
            } else {
              setArtistRoyalties(null);
            }
          } catch (e) {
            console.error('Failed to fetch artist royalties:', e.message);
            setArtistRoyalties(null);
          }
        } else {
          setArtistRoyalties(null);
        }
      }

    } catch (error) {
      
      toast.error('Failed to load record label data');
    } finally {
      setLoading(false);
    }
  }, [selectedArtist, selectedLabelArtist, user?.role, user?.id, isManager]);

  useEffect(() => {
    fetchData();
    fetchExchangeRates();
  }, [fetchData, fetchExchangeRates]);

  const handleAddTransaction = async (e) => {
    e.preventDefault();
    
    if (!formData.artist_id) {
      toast.error('Please select an artist');
      return;
    }
    if (!formData.amount || parseFloat(formData.amount) <= 0) {
      toast.error('Please enter a valid amount');
      return;
    }
    if (!formData.description.trim()) {
      toast.error('Please enter a description');
      return;
    }

    try {
      const token = localStorage.getItem('token');
      await axios.post(`${API}/label/transactions`, {
        ...formData,
        transaction_type: transactionType,
        amount: parseFloat(formData.amount),
        date: new Date(formData.date).toISOString()
      }, {
        headers: { Authorization: `Bearer ${token}` }
      });

      toast.success(`${transactionType === 'cost' ? 'Cost' : transactionType === 'income' ? 'Income' : 'Drawdown'} added successfully!`);
      setAddDialogOpen(false);
      setFormData({
        artist_id: '',
        amount: '',
        description: '',
        category: '',
        date: new Date().toISOString().split('T')[0],
        notes: ''
      });
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to add transaction');
    }
  };

  const openDealSettings = (artist) => {
    setSelectedArtistForSettings(artist);
    // Use label_profit_split as source of truth, fall back to artist_split_percentage
    const splitPercentage = artist.label_profit_split ?? artist.artist_split_percentage ?? 50;
    setDealSettings({
      distributor_fee_percentage: artist.distributor_fee_percentage || 25,
      underplay_paythrough_percentage: artist.underplay_paythrough_percentage || 30,
      artist_split_percentage: splitPercentage,
      cost_recoupment_model: artist.cost_recoupment_model || 'costs_off_top',
      term_years: artist.term_years || '',
      term_start_date: artist.term_start_date ? new Date(artist.term_start_date).toISOString().split('T')[0] : ''
    });
    fetchRoyaltyParticipants(artist.id);
    setShowDealSettings(true);
  };

  const fetchRoyaltyParticipants = async (artistId) => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API}/label/royalty-participants?artist_id=${artistId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setRoyaltyParticipants(response.data);
    } catch (error) {
      console.error('Failed to fetch royalty participants:', error.message);
      // Silent fail - royalty participants is supplementary data
    }
  };

  const saveDealSettings = async () => {
    if (!selectedArtistForSettings) return;
    
    try {
      const token = localStorage.getItem('token');
      const payload = {
        ...dealSettings,
        term_start_date: dealSettings.term_start_date ? new Date(dealSettings.term_start_date).toISOString() : null
      };
      await axios.put(`${API}/label/artists/${selectedArtistForSettings.id}/settings`, payload, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('Deal settings saved!');
      setShowDealSettings(false);
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to save settings');
    }
  };

  const addRoyaltyParticipant = async (e) => {
    e.preventDefault();
    if (!selectedArtistForSettings) return;
    
    try {
      const token = localStorage.getItem('token');
      await axios.post(`${API}/label/royalty-participants`, {
        artist_id: selectedArtistForSettings.id,
        name: participantForm.name,
        role: participantForm.role,
        percentage: parseFloat(participantForm.percentage) || 0,
        advance_paid: parseFloat(participantForm.advance_paid) || 0,
        notes: participantForm.notes || null
      }, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('Royalty participant added!');
      setShowParticipantDialog(false);
      setParticipantForm({ name: '', role: 'Producer', percentage: '', advance_paid: '', notes: '' });
      fetchRoyaltyParticipants(selectedArtistForSettings.id);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to add participant');
    }
  };

  const deleteRoyaltyParticipant = async (participantId) => {
    try {
      const token = localStorage.getItem('token');
      await axios.delete(`${API}/label/royalty-participants/${participantId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('Participant removed');
      fetchRoyaltyParticipants(selectedArtistForSettings.id);
    } catch (error) {
      toast.error('Failed to remove participant');
    }
  };

  const confirmDelete = (transaction) => {
    setTransactionToDelete(transaction);
    setDeleteDialogOpen(true);
  };

  const handleDelete = async () => {
    if (!transactionToDelete) return;
    
    try {
      const token = localStorage.getItem('token');
      await axios.delete(`${API}/label/transactions/${transactionToDelete.id}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('Transaction deleted');
      setDeleteDialogOpen(false);
      setTransactionToDelete(null);
      fetchData();
    } catch (error) {
      toast.error('Failed to delete transaction');
    }
  };

  const handleEdit = (transaction) => {
    setEditingTransaction(transaction);
    setTransactionType(transaction.type);
    setFormData({
      artist_id: transaction.artist_id,
      amount: Math.abs(transaction.amount).toString(),
      description: transaction.description || '',
      category: transaction.category || '',
      date: transaction.date ? transaction.date.split('T')[0] : new Date().toISOString().split('T')[0],
      notes: transaction.notes || ''
    });
    setEditDialogOpen(true);
  };

  const handleUpdate = async (e) => {
    e.preventDefault();
    
    try {
      const token = localStorage.getItem('token');
      const amount = parseFloat(formData.amount);
      
      await axios.put(`${API}/label/transactions/${editingTransaction.id}`, {
        ...formData,
        type: transactionType,
        amount: transactionType === 'cost' ? -Math.abs(amount) : Math.abs(amount)
      }, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      toast.success('Transaction updated successfully!');
      setEditDialogOpen(false);
      setEditingTransaction(null);
      setFormData({
        artist_id: '',
        amount: '',
        description: '',
        category: '',
        date: new Date().toISOString().split('T')[0],
        notes: ''
      });
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to update transaction');
    }
  };

  const getArtistName = (artistId) => {
    const artist = labelArtists.find(a => a.id === artistId);
    return artist?.name || 'Unknown';
  };

  // Handle royalty payout recording
  const handleRecordPayout = async (e) => {
    e.preventDefault();
    
    const currentArtistId = selectedLabelArtist !== 'all' ? selectedLabelArtist : selectedArtist;
    if (!currentArtistId || currentArtistId === 'all' || currentArtistId === 'management') {
      toast.error('Please select an artist first');
      return;
    }
    
    try {
      const token = localStorage.getItem('token');
      const amount = parseFloat(payoutForm.amount);
      
      if (isNaN(amount) || amount <= 0) {
        toast.error('Please enter a valid payout amount');
        return;
      }
      
      // Create a payout transaction (negative cost - represents money going out to artist)
      await axios.post(`${API}/label/transactions`, {
        artist_id: currentArtistId,
        amount: -Math.abs(amount),
        transaction_type: 'cost',
        description: `Royalty Payout to Artist${payoutForm.notes ? ` - ${payoutForm.notes}` : ''}`,
        category: 'Royalty Payout',
        date: payoutForm.date,
        notes: payoutForm.notes
      }, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      toast.success('Royalty payout recorded successfully!');
      setPayoutDialogOpen(false);
      setPayoutForm({ amount: '', notes: '', date: new Date().toISOString().split('T')[0] });
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to record payout');
    }
  };

  // Handle importing royalty as income transaction
  const handleImportRoyalty = async () => {
    const currentArtistId = selectedLabelArtist !== 'all' ? selectedLabelArtist : selectedArtist;
    if (!currentArtistId || currentArtistId === 'all' || currentArtistId === 'management') {
      toast.error('Please select an artist first');
      return;
    }
    
    if (!artistRoyalties || artistRoyalties.total_revenue <= 0) {
      toast.error('No royalty data to import');
      return;
    }
    
    try {
      const token = localStorage.getItem('token');
      const artistName = labelArtists.find(a => a.id === currentArtistId)?.name || 'Artist';
      
      // Create an income transaction for the royalty amount
      await axios.post(`${API}/label/transactions`, {
        artist_id: currentArtistId,
        amount: artistRoyalties.total_revenue,
        transaction_type: 'income',
        description: `Virgin DSR Royalty Import - ${artistName}`,
        category: 'Streaming',
        date: new Date().toISOString().split('T')[0],
        notes: `Imported from DSR: ${artistRoyalties.total_streams?.toLocaleString() || 0} streams, ${artistRoyalties.track_count || 0} tracks`
      }, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      toast.success(`Royalty of ${formatCurrency(artistRoyalties.total_revenue)} imported as income!`);
      setImportRoyaltyDialogOpen(false);
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to import royalty');
    }
  };

  const formatCurrency = (amount, currency = 'GBP') => {
    const symbol = getCurrencySymbol(currency);
    return `${symbol}${Math.abs(amount).toLocaleString('en-GB', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
  };

  // Format currency with sign (for balance display)
  const formatSignedCurrency = (amount, currency = 'GBP') => {
    const symbol = getCurrencySymbol(currency);
    const formattedAmount = Math.abs(amount).toLocaleString('en-GB', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
    if (amount < 0) {
      return `-${symbol}${formattedAmount}`;
    } else if (amount > 0) {
      return `+${symbol}${formattedAmount}`;
    }
    return `${symbol}${formattedAmount}`;
  };

  // Format with converted amount
  const formatWithConversion = (amountGBP) => {
    if (displayCurrency === 'GBP') {
      return formatCurrency(amountGBP, 'GBP');
    }
    const converted = convertAmount(amountGBP, displayCurrency);
    return `${formatCurrency(converted, displayCurrency)} (£${Math.abs(amountGBP).toLocaleString('en-GB', { minimumFractionDigits: 2, maximumFractionDigits: 2 })})`;
  };

  // Export transactions to simple text format
  const exportTransactions = (exportType) => {
    // Filter transactions based on export type
    let filteredTransactions = transactions;
    if (exportType === 'costs') {
      filteredTransactions = transactions.filter(t => t.transaction_type === 'cost');
    } else if (exportType === 'income') {
      filteredTransactions = transactions.filter(t => t.transaction_type === 'income');
    }

    if (filteredTransactions.length === 0) {
      toast.error(`No ${exportType === 'both' ? 'transactions' : exportType} to export`);
      return;
    }

    // Sort by date
    filteredTransactions.sort((a, b) => new Date(a.date) - new Date(b.date));

    // Get artist name for the export
    let artistName = 'All Label Artists';
    if (selectedLabelArtist && selectedLabelArtist !== 'all') {
      const artist = labelArtists.find(a => a.id === selectedLabelArtist);
      artistName = artist?.name || 'Unknown Artist';
    }

    // Build text content
    let textContent = `RECORD LABEL ${exportType.toUpperCase()}\n`;
    textContent += `Artist: ${artistName}\n`;
    textContent += `Exported: ${new Date().toLocaleDateString('en-GB')}\n`;
    textContent += `${'='.repeat(50)}\n\n`;

    // Calculate totals
    let totalCosts = 0;
    let totalIncome = 0;

    filteredTransactions.forEach((t) => {
      const date = new Date(t.date).toLocaleDateString('en-GB');
      const type = t.transaction_type === 'cost' ? 'COST' : 'INCOME';
      const amount = Math.abs(t.amount);
      
      if (t.transaction_type === 'cost') {
        totalCosts += amount;
      } else {
        totalIncome += amount;
      }

      // Get artist name for this transaction if viewing all
      let transactionArtist = '';
      if (selectedLabelArtist === 'all') {
        const artist = labelArtists.find(a => a.id === t.artist_id);
        transactionArtist = artist ? `[${artist.name}] ` : '';
      }

      textContent += `${date} - ${type}\n`;
      textContent += `${transactionArtist}${t.category}: ${t.description}\n`;
      textContent += `Amount: £${amount.toFixed(2)}\n`;
      if (t.notes) {
        textContent += `Notes: ${t.notes}\n`;
      }
      textContent += `${'-'.repeat(30)}\n\n`;
    });

    // Summary
    textContent += `${'='.repeat(50)}\n`;
    textContent += `SUMMARY\n`;
    textContent += `${'='.repeat(50)}\n`;
    if (exportType === 'both' || exportType === 'income') {
      textContent += `Total Income: £${totalIncome.toFixed(2)}\n`;
    }
    if (exportType === 'both' || exportType === 'costs') {
      textContent += `Total Costs: £${totalCosts.toFixed(2)}\n`;
    }
    if (exportType === 'both') {
      const balance = totalIncome - totalCosts;
      textContent += `Balance: ${balance >= 0 ? '' : '-'}£${Math.abs(balance).toFixed(2)}\n`;
    }
    textContent += `Total Records: ${filteredTransactions.length}\n`;

    // Create and download file
    const blob = new Blob([textContent], { type: 'text/plain' });
    const url = window.URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    const filename = `label_${exportType}_${artistName.replace(/\s+/g, '_')}_${new Date().toISOString().split('T')[0]}.txt`;
    link.setAttribute('download', filename);
    document.body.appendChild(link);
    link.click();
    link.remove();
    window.URL.revokeObjectURL(url);

    toast.success(`${exportType.charAt(0).toUpperCase() + exportType.slice(1)} exported successfully!`);
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
      </div>
    );
  }

  // Check if user is a label artist (for artist view)
  const isLabelArtist = user?.role === 'artist' && labelArtists.some(a => a.id === user.id);

  // If artist but not a label artist, show message
  if (user?.role === 'artist' && !isLabelArtist) {
    return (
      <div className="space-y-6" data-testid="record-label-page">
        <div>
          <h1 className="font-manrope font-extrabold text-3xl text-foreground">Record Label</h1>
          <p className="text-muted-foreground text-sm mt-1">Track label costs and income</p>
        </div>
        <Card className="bg-card border-border/50 p-8 text-center">
          <Music className="w-12 h-12 mx-auto text-muted-foreground mb-4" />
          <h3 className="text-lg font-semibold mb-2">Not a Label Artist</h3>
          <p className="text-muted-foreground">
            This section is for artists signed to the Underplay record label.
          </p>
        </Card>
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="record-label-page">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="font-manrope font-extrabold text-3xl text-foreground">Record Label</h1>
          <p className="text-muted-foreground text-sm mt-1">Track label costs and income</p>
        </div>
        
        {/* Artist Filter Dropdown */}
        {isManager && labelArtists.length > 0 && (
          <div className="flex items-center gap-3">
            <select
              value={selectedLabelArtist}
              onChange={(e) => setSelectedLabelArtist(e.target.value)}
              className="px-4 py-2 bg-input/50 border border-border text-foreground rounded-xl focus:border-primary focus:ring-2 focus:ring-primary/20 outline-none text-sm"
            >
              <option value="all">All Label Artists</option>
              {labelArtists.map((artist) => (
                <option key={artist.id} value={artist.id}>{artist.name}</option>
              ))}
            </select>
          </div>
        )}
      </div>
      
      {/* Action Buttons */}
      {isManager && (
          <div className="flex flex-wrap gap-2">
            <Dialog open={addDialogOpen} onOpenChange={setAddDialogOpen}>
              <DialogTrigger asChild>
                <Button 
                  onClick={() => setTransactionType('drawdown')}
                  className="flex items-center gap-2 bg-blue-600 hover:bg-blue-700"
                >
                  <ArrowDownCircle className="w-4 h-4" />
                  Add Drawdown
                </Button>
              </DialogTrigger>
              <DialogTrigger asChild>
                <Button 
                  onClick={() => setTransactionType('label_investment')}
                  variant="outline"
                  className="flex items-center gap-2 border-purple-500/50 text-purple-500 hover:bg-purple-500/10"
                >
                  <PoundSterling className="w-4 h-4" />
                  Label Investment
                </Button>
              </DialogTrigger>
              <DialogTrigger asChild>
                <Button 
                  onClick={() => setTransactionType('artist_investment')}
                  variant="outline"
                  className="flex items-center gap-2 border-cyan-500/50 text-cyan-500 hover:bg-cyan-500/10"
                >
                  <PoundSterling className="w-4 h-4" />
                  Artist Investment
                </Button>
              </DialogTrigger>
              <div className="w-px h-8 bg-border mx-1" />
              <DialogTrigger asChild>
                <Button 
                  onClick={() => setTransactionType('cost')}
                  variant="outline"
                  className="flex items-center gap-2 border-red-500/50 text-red-500 hover:bg-red-500/10"
                >
                  <ArrowDownCircle className="w-4 h-4" />
                  Add Cost
                </Button>
              </DialogTrigger>
              <DialogTrigger asChild>
                <Button 
                  onClick={() => setTransactionType('income')}
                  className="flex items-center gap-2 bg-green-600 hover:bg-green-700"
                >
                  <ArrowUpCircle className="w-4 h-4" />
                  Add Income
                </Button>
              </DialogTrigger>
              
              {/* Export Buttons */}
              {transactions.length > 0 && (
                <>
                  <div className="w-px h-8 bg-border mx-1" />
                  <Button
                    variant="outline"
                    onClick={() => exportTransactions('costs')}
                    className="flex items-center gap-2"
                    data-testid="export-costs-btn"
                  >
                    <Download className="w-4 h-4" />
                    Export Costs
                  </Button>
                  <Button
                    variant="outline"
                    onClick={() => exportTransactions('income')}
                    className="flex items-center gap-2"
                    data-testid="export-income-btn"
                  >
                    <Download className="w-4 h-4" />
                    Export Income
                  </Button>
                  <Button
                    variant="outline"
                    onClick={() => exportTransactions('both')}
                    className="flex items-center gap-2"
                    data-testid="export-all-btn"
                  >
                    <Download className="w-4 h-4" />
                    Export All
                  </Button>
                  <div className="w-px h-8 bg-border mx-1" />
                  {/* Currency Selector */}
                  <div className="flex items-center gap-2">
                    <span className="text-xs text-muted-foreground">View in:</span>
                    <select
                      value={displayCurrency}
                      onChange={(e) => setDisplayCurrency(e.target.value)}
                      className="h-8 px-2 bg-input/50 border border-border text-foreground rounded-lg text-sm"
                      data-testid="currency-selector"
                    >
                      {currencies.map((c) => (
                        <option key={c.code} value={c.code}>
                          {c.symbol} {c.code}
                        </option>
                      ))}
                    </select>
                  </div>
                </>
              )}
              <DialogContent className="bg-card border-border/50 max-w-md">
                <DialogHeader>
                  <DialogTitle className="font-manrope font-bold text-xl flex items-center gap-2">
                    {transactionType === 'cost' ? (
                      <><ArrowDownCircle className="w-5 h-5 text-red-500" /> Add Cost</>
                    ) : transactionType === 'income' ? (
                      <><ArrowUpCircle className="w-5 h-5 text-green-500" /> Add Income</>
                    ) : transactionType === 'label_investment' ? (
                      <><PoundSterling className="w-5 h-5 text-purple-500" /> Add Label Investment</>
                    ) : transactionType === 'artist_investment' ? (
                      <><PoundSterling className="w-5 h-5 text-cyan-500" /> Add Artist Investment</>
                    ) : (
                      <><ArrowDownCircle className="w-5 h-5 text-blue-500" /> Add Drawdown</>
                    )}
                  </DialogTitle>
                  {transactionType === 'drawdown' && (
                    <p className="text-sm text-muted-foreground">
                      Record money received from Virgin/Universal
                    </p>
                  )}
                  {transactionType === 'label_investment' && (
                    <p className="text-sm text-muted-foreground">
                      Record money Underplay has invested (your own funds)
                    </p>
                  )}
                  {transactionType === 'artist_investment' && (
                    <p className="text-sm text-muted-foreground">
                      Record money the artist/band has contributed
                    </p>
                  )}
                </DialogHeader>
                <form onSubmit={handleAddTransaction} className="space-y-4">
                  <div className="space-y-2">
                    <Label>Artist</Label>
                    <select
                      value={formData.artist_id}
                      onChange={(e) => setFormData({ ...formData, artist_id: e.target.value })}
                      className="w-full px-4 py-2 bg-input/50 border border-border text-foreground rounded-xl focus:border-primary focus:ring-2 focus:ring-primary/20 outline-none"
                      required
                    >
                      <option value="">Select Artist</option>
                      {labelArtists.map((artist) => (
                        <option key={artist.id} value={artist.id}>{artist.name}</option>
                      ))}
                    </select>
                  </div>

                  <div className="grid grid-cols-2 gap-4">
                    <div className="space-y-2">
                      <Label>Amount (£)</Label>
                      <Input
                        type="number"
                        step="0.01"
                        min="0"
                        value={formData.amount}
                        onChange={(e) => setFormData({ ...formData, amount: e.target.value })}
                        placeholder="0.00"
                        className="bg-input/50 border-border rounded-xl"
                        required
                      />
                    </div>
                    <div className="space-y-2">
                      <Label>Date</Label>
                      <Input
                        type="date"
                        value={formData.date}
                        onChange={(e) => setFormData({ ...formData, date: e.target.value })}
                        className="bg-input/50 border-border rounded-xl"
                        required
                      />
                    </div>
                  </div>

                  <div className="space-y-2">
                    <Label>Category</Label>
                    <select
                      value={formData.category}
                      onChange={(e) => setFormData({ ...formData, category: e.target.value })}
                      className="w-full px-4 py-2 bg-input/50 border border-border text-foreground rounded-xl focus:border-primary focus:ring-2 focus:ring-primary/20 outline-none"
                      required
                    >
                      <option value="">Select Category</option>
                      {(transactionType === 'cost' ? costCategories : transactionType === 'income' ? incomeCategories : drawdownCategories).map((cat) => (
                        <option key={cat} value={cat}>{cat}</option>
                      ))}
                    </select>
                  </div>

                  <div className="space-y-2">
                    <Label>Description</Label>
                    <Input
                      value={formData.description}
                      onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                      placeholder={transactionType === 'cost' ? 'e.g., Studio session for album' : transactionType === 'income' ? 'e.g., Q4 streaming royalties' : 'e.g., Q1 2026 drawdown from Virgin'}
                      className="bg-input/50 border-border rounded-xl"
                      required
                    />
                  </div>

                  <div className="space-y-2">
                    <Label>Notes (Optional)</Label>
                    <Textarea
                      value={formData.notes}
                      onChange={(e) => setFormData({ ...formData, notes: e.target.value })}
                      placeholder="Any additional details..."
                      className="bg-input/50 border-border rounded-xl"
                      rows={2}
                    />
                  </div>

                  <Button type="submit" className="w-full">
                    Add {transactionType === 'cost' ? 'Cost' : transactionType === 'income' ? 'Income' : transactionType === 'label_investment' ? 'Label Investment' : transactionType === 'artist_investment' ? 'Artist Investment' : 'Drawdown'}
                  </Button>
                </form>
              </DialogContent>
            </Dialog>

            {/* Edit Transaction Dialog */}
            <Dialog open={editDialogOpen} onOpenChange={(open) => {
              setEditDialogOpen(open);
              if (!open) {
                setEditingTransaction(null);
                setFormData({
                  artist_id: '',
                  amount: '',
                  description: '',
                  category: '',
                  date: new Date().toISOString().split('T')[0],
                  notes: ''
                });
              }
            }}>
              <DialogContent className="bg-card border-border max-w-md">
                <DialogHeader>
                  <DialogTitle>Edit {transactionType === 'cost' ? 'Cost' : 'Income'}</DialogTitle>
                </DialogHeader>
                <form onSubmit={handleUpdate} className="space-y-4">
                  <div className="flex gap-2 mb-4">
                    <Button
                      type="button"
                      variant={transactionType === 'cost' ? 'default' : 'outline'}
                      onClick={() => setTransactionType('cost')}
                      className="flex-1"
                    >
                      <ArrowDownCircle className="w-4 h-4 mr-2" />
                      Cost
                    </Button>
                    <Button
                      type="button"
                      variant={transactionType === 'income' ? 'default' : 'outline'}
                      onClick={() => setTransactionType('income')}
                      className="flex-1"
                    >
                      <ArrowUpCircle className="w-4 h-4 mr-2" />
                      Income
                    </Button>
                  </div>

                  <div>
                    <Label>Artist</Label>
                    <select
                      className="w-full mt-1 px-3 py-2 bg-background border border-input rounded-md"
                      value={formData.artist_id}
                      onChange={(e) => setFormData({ ...formData, artist_id: e.target.value })}
                      required
                    >
                      <option value="">Select Artist</option>
                      {labelArtists.map(artist => (
                        <option key={artist.id} value={artist.id}>{artist.name}</option>
                      ))}
                    </select>
                  </div>

                  <div>
                    <Label>Amount (£)</Label>
                    <Input
                      type="number"
                      step="0.01"
                      value={formData.amount}
                      onChange={(e) => setFormData({ ...formData, amount: e.target.value })}
                      placeholder="0.00"
                      required
                    />
                  </div>

                  <div>
                    <Label>Category</Label>
                    <select
                      className="w-full mt-1 px-3 py-2 bg-background border border-input rounded-md"
                      value={formData.category}
                      onChange={(e) => setFormData({ ...formData, category: e.target.value })}
                    >
                      <option value="">Select Category</option>
                      {(transactionType === 'cost' ? costCategories : incomeCategories).map(cat => (
                        <option key={cat} value={cat}>{cat}</option>
                      ))}
                    </select>
                  </div>

                  <div>
                    <Label>Description</Label>
                    <Input
                      value={formData.description}
                      onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                      placeholder="Brief description"
                      required
                    />
                  </div>

                  <div>
                    <Label>Date</Label>
                    <Input
                      type="date"
                      value={formData.date}
                      onChange={(e) => setFormData({ ...formData, date: e.target.value })}
                      required
                    />
                  </div>

                  <div>
                    <Label>Notes (Optional)</Label>
                    <Textarea
                      value={formData.notes}
                      onChange={(e) => setFormData({ ...formData, notes: e.target.value })}
                      placeholder="Additional notes..."
                      rows={2}
                    />
                  </div>

                  <Button type="submit" className="w-full">
                    Update {transactionType === 'cost' ? 'Cost' : 'Income'}
                  </Button>
                </form>
              </DialogContent>
            </Dialog>
          </div>
        )}

      {/* Summary Cards - For specific artist or artist view */}
      {artistSummary && (
        <div className="space-y-4">
          {/* Financial Flow Breakdown */}
          <Card className="bg-card border-border/50 p-5" data-testid="financial-breakdown-card">
            <h3 className="font-semibold text-base mb-4 flex items-center gap-2">
              <PoundSterling className="w-5 h-5 text-primary" />
              Financial Breakdown
            </h3>
            
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              {/* Left Column - Budget & Spending */}
              <div className="space-y-3">
                <h4 className="text-sm font-medium text-muted-foreground uppercase tracking-wide">Budget &amp; Investment</h4>
                
                {/* Drawdown - The budget from Virgin */}
                <div className="bg-blue-500/10 border border-blue-500/30 rounded-lg p-3">
                  <div className="flex justify-between items-center">
                    <span className="text-sm font-medium text-blue-400">Virgin Drawdown</span>
                    <span className="font-bold text-blue-400">{formatCurrency(artistSummary.total_drawdown || 0)}</span>
                  </div>
                  <p className="text-xs text-muted-foreground mt-1">Advance from distributor</p>
                </div>
                
                {/* Label Investment - Underplay's own money */}
                {(artistSummary.label_investment > 0 || isManager) && (
                  <div className="bg-purple-500/10 border border-purple-500/30 rounded-lg p-3">
                    <div className="flex justify-between items-center">
                      <span className="text-sm font-medium text-purple-400">Label Investment</span>
                      <span className="font-bold text-purple-400">{formatCurrency(artistSummary.label_investment || 0)}</span>
                    </div>
                    <p className="text-xs text-muted-foreground mt-1">Underplay's own funds</p>
                  </div>
                )}
                
                {/* Artist Investment - Band's own money */}
                {(artistSummary.artist_investment > 0 || true) && (
                  <div className="bg-cyan-500/10 border border-cyan-500/30 rounded-lg p-3">
                    <div className="flex justify-between items-center">
                      <span className="text-sm font-medium text-cyan-400">Artist Investment</span>
                      <span className="font-bold text-cyan-400">{formatCurrency(artistSummary.artist_investment || 0)}</span>
                    </div>
                    <p className="text-xs text-muted-foreground mt-1">Band's own contribution</p>
                  </div>
                )}
                
                {/* Costs spent */}
                <div className="bg-red-500/10 border border-red-500/30 rounded-lg p-3">
                  <div className="flex justify-between items-center">
                    <span className="text-sm font-medium text-red-400">Costs Spent</span>
                    <span className="font-bold text-red-400">-{formatCurrency(artistSummary.total_costs || 0)}</span>
                  </div>
                  <p className="text-xs text-muted-foreground mt-1">Studio, marketing, etc.</p>
                </div>
                
                {/* Total Budget & Remaining */}
                <div className={`rounded-lg p-3 ${(artistSummary.remaining_budget || 0) >= 0 ? 'bg-green-500/10 border border-green-500/30' : 'bg-orange-500/10 border border-orange-500/30'}`}>
                  <div className="flex justify-between items-center text-xs text-muted-foreground mb-1">
                    <span>Total Budget</span>
                    <span>{formatCurrency(artistSummary.total_budget || artistSummary.total_drawdown || 0)}</span>
                  </div>
                  <div className="flex justify-between items-center">
                    <span className="text-sm font-medium">Remaining</span>
                    <span className={`font-bold ${(artistSummary.remaining_budget || 0) >= 0 ? 'text-green-400' : 'text-orange-400'}`}>
                      {formatCurrency(artistSummary.remaining_budget || 0)}
                    </span>
                  </div>
                </div>
              </div>
              
              {/* Right Column - Income & Recoupment */}
              <div className="space-y-3">
                <h4 className="text-sm font-medium text-muted-foreground uppercase tracking-wide">Recoupment</h4>
                
                {/* Remaining to Recoup - Decreases as income comes in */}
                <div className={`rounded-lg p-3 ${
                  artistSummary.recouped 
                    ? 'bg-green-500/10 border border-green-500/30' 
                    : 'bg-red-500/10 border border-red-500/30'
                }`}>
                  <div className="flex justify-between items-center">
                    <span className={`text-sm font-medium ${artistSummary.recouped ? 'text-green-400' : 'text-red-400'}`}>
                      {artistSummary.recouped ? 'Fully Recouped!' : 'Still to Recoup'}
                    </span>
                    <span className={`font-bold ${artistSummary.recouped ? 'text-green-400' : 'text-red-400'}`}>
                      {artistSummary.recouped ? '✓ £0.00' : formatCurrency(artistSummary.remaining_to_recoup || artistSummary.total_drawdown || 0)}
                    </span>
                  </div>
                  <p className="text-xs text-muted-foreground mt-1">
                    {artistSummary.recouped 
                      ? 'All advances paid back from royalties' 
                      : `Original drawdown: ${formatCurrency(artistSummary.total_drawdown || 0)}`}
                  </p>
                </div>
                
                {/* Income received */}
                <div className="bg-green-500/10 border border-green-500/30 rounded-lg p-3">
                  <div className="flex justify-between items-center">
                    <span className="text-sm font-medium text-green-400">Income Received</span>
                    <span className="font-bold text-green-400">{formatCurrency(artistSummary.total_income || 0)}</span>
                  </div>
                  <p className="text-xs text-muted-foreground mt-1">Royalties, syncs, PPL (net from Virgin)</p>
                </div>
                
                {/* Virgin Royalties - from DSR uploads (Artist-specific) */}
                {artistRoyalties && artistRoyalties.total_revenue > 0 && (
                  <div 
                    className="bg-amber-500/10 border border-amber-500/30 rounded-lg p-3 cursor-pointer hover:border-amber-500/50 transition-colors" 
                    data-testid="virgin-royalties-tile"
                    onClick={() => navigate('/royalties')}
                  >
                    <div className="flex justify-between items-center">
                      <span className="text-sm font-medium text-amber-400 flex items-center gap-1">
                        Virgin Royalties
                        <ExternalLink className="w-3 h-3" />
                      </span>
                      <span className="font-bold text-amber-400">{formatCurrency(artistRoyalties.total_revenue || 0)}</span>
                    </div>
                    <p className="text-xs text-muted-foreground mt-1">
                      {artistRoyalties.total_streams?.toLocaleString()} streams • {artistRoyalties.track_count || 0} tracks
                    </p>
                    {/* Import and Payout buttons */}
                    <div className="flex gap-2 mt-2">
                      <Button
                        variant="outline"
                        size="sm"
                        className="text-xs h-7 text-amber-400 border-amber-500/30 hover:bg-amber-500/10"
                        onClick={(e) => {
                          e.stopPropagation();
                          setImportRoyaltyDialogOpen(true);
                        }}
                      >
                        <Import className="w-3 h-3 mr-1" />
                        Import as Income
                      </Button>
                      <Button
                        variant="outline"
                        size="sm"
                        className="text-xs h-7 text-green-400 border-green-500/30 hover:bg-green-500/10"
                        onClick={(e) => {
                          e.stopPropagation();
                          setPayoutDialogOpen(true);
                        }}
                      >
                        <Banknote className="w-3 h-3 mr-1" />
                        Record Payout
                      </Button>
                    </div>
                  </div>
                )}
                
                {/* Balance - Red for unrecouped, Green for profit */}
                <div className={`rounded-lg p-3 ${
                  artistSummary.recouped 
                    ? 'bg-green-500/10 border border-green-500/30' 
                    : 'bg-red-500/10 border border-red-500/30'
                }`}>
                  <div className="flex justify-between items-center">
                    <span className="text-sm font-medium">Balance</span>
                    <span className={`font-bold ${artistSummary.recouped ? 'text-green-400' : 'text-red-400'}`}>
                      {formatSignedCurrency(artistSummary.balance || 0)}
                    </span>
                  </div>
                  <p className="text-xs text-muted-foreground mt-1">
                    {artistSummary.recouped 
                      ? '✓ In profit! Ready to split' 
                      : 'Income - Drawdown (negative = still recouping)'}
                  </p>
                </div>
              </div>
            </div>
            
            {/* Profit Split Section - Only shows if recouped */}
            {artistSummary.recouped && (artistSummary.balance || 0) > 0 && (
              <div className="mt-4 pt-4 border-t border-border">
                <div className="flex items-center justify-between mb-3">
                  <h4 className="text-sm font-medium text-muted-foreground uppercase tracking-wide">Profit Split ({artistSummary.artist_split_percentage || 50}/{100 - (artistSummary.artist_split_percentage || 50)})</h4>
                  {isManager && (
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => {
                        const artist = labelArtists.find(a => a.id === selectedLabelArtist);
                        if (artist) openDealSettings(artist);
                      }}
                      className="text-xs text-muted-foreground hover:text-foreground"
                    >
                      <Settings className="w-3 h-3 mr-1" />
                      Edit Split
                    </Button>
                  )}
                </div>
                <div className="grid grid-cols-2 gap-4">
                  <div className="bg-primary/10 border border-primary/30 rounded-lg p-3">
                    <div className="flex justify-between items-center">
                      <span className="text-sm font-medium">Artist Share</span>
                      <span className="font-bold text-primary">{formatCurrency(artistSummary.artist_share_after_participants !== undefined ? artistSummary.artist_share_after_participants : artistSummary.artist_share || 0)}</span>
                    </div>
                  </div>
                  {isManager && (
                    <div className="bg-muted/30 border border-border rounded-lg p-3">
                      <div className="flex justify-between items-center">
                        <span className="text-sm font-medium">Label Share</span>
                        <span className="font-bold">{formatCurrency(artistSummary.label_share || 0)}</span>
                      </div>
                      {artistSummary.underplay_paythrough > 0 && (
                        <p className="text-xs text-purple-400 mt-1">
                          Paythrough: {formatCurrency(artistSummary.underplay_paythrough)}
                        </p>
                      )}
                    </div>
                  )}
                </div>
              </div>
            )}
            
            {/* Always show Deal Settings section for managers */}
            {isManager && (
              <div className="mt-4 pt-4 border-t border-border">
                <div className="flex items-center justify-between">
                  <p className="text-sm text-muted-foreground">
                    Deal Split: {artistSummary.artist_split_percentage || 50}% to Artist / {100 - (artistSummary.artist_split_percentage || 50)}% to Label
                  </p>
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => {
                      const artist = labelArtists.find(a => a.id === selectedLabelArtist);
                      if (artist) {
                        openDealSettings(artist);
                      } else {
                        // Fallback - create a minimal artist object
                        openDealSettings({ 
                          id: selectedLabelArtist, 
                          name: 'Artist',
                          artist_split_percentage: artistSummary.artist_split_percentage || 50,
                          cost_recoupment_model: artistSummary.cost_recoupment_model || 'costs_off_top'
                        });
                      }
                    }}
                    className="text-xs"
                  >
                    <Settings className="w-3 h-3 mr-1" />
                    Edit Deal Settings
                  </Button>
                </div>
              </div>
            )}
          </Card>

          {/* Quick Stats Cards */}
          <div className="grid grid-cols-2 md:grid-cols-5 gap-4">
            <Card className="bg-blue-500/10 border-blue-500/30 p-4" data-testid="drawdown-card">
              <div className="flex items-center gap-2 text-blue-500 mb-1">
                <ArrowDownCircle className="w-4 h-4" />
                <span className="text-sm font-medium">Drawdown</span>
              </div>
              <p className="text-2xl font-bold">{formatCurrency(artistSummary.total_drawdown || 0)}</p>
              <p className="text-xs text-muted-foreground mt-1">
                Spent: {formatCurrency(artistSummary.total_costs || 0)}
              </p>
            </Card>
            
            <Card className="bg-green-500/10 border-green-500/30 p-4" data-testid="total-income-card">
              <div className="flex items-center gap-2 text-green-500 mb-1">
                <ArrowUpCircle className="w-4 h-4" />
                <span className="text-sm font-medium">Income</span>
              </div>
              <p className="text-2xl font-bold">{formatCurrency(artistSummary.total_income || 0)}</p>
              <p className="text-xs text-muted-foreground mt-1">Net from Virgin</p>
            </Card>
            
            <Card className={`border-border/50 p-4 ${artistSummary.recouped ? 'bg-green-500/10 border-green-500/30' : 'bg-red-500/10 border-red-500/30'}`} data-testid="recoup-status-card">
              <div className="flex items-center gap-2 mb-1">
                {artistSummary.recouped ? (
                  <TrendingUp className="w-4 h-4 text-green-500" />
                ) : (
                  <TrendingDown className="w-4 h-4 text-red-500" />
                )}
                <span className="text-sm font-medium">Balance</span>
              </div>
              <p className={`text-lg font-bold ${artistSummary.recouped ? 'text-green-500' : 'text-red-500'}`}>
                {formatSignedCurrency(artistSummary.balance || 0)}
              </p>
              <p className="text-xs text-muted-foreground mt-1">
                {artistSummary.recouped ? '✓ In Profit' : `${formatCurrency(artistSummary.remaining_to_recoup || 0)} to recoup`}
              </p>
            </Card>
            
            <Card className={`p-4 ${artistSummary.recouped ? 'bg-primary/10 border-primary/30' : 'bg-orange-500/10 border-orange-500/30'}`} data-testid="artist-share-card">
              <div className={`flex items-center gap-2 mb-1 ${artistSummary.recouped ? 'text-primary' : 'text-orange-500'}`}>
                <PoundSterling className="w-4 h-4" />
                <span className="text-sm font-medium">Artist ({artistSummary.artist_split_percentage || 50}%)</span>
              </div>
              <p className={`text-2xl font-bold ${artistSummary.recouped ? '' : 'text-orange-500'}`}>
                {formatWithConversion(artistSummary.artist_share_after_participants !== undefined ? artistSummary.artist_share_after_participants : artistSummary.artist_share || 0)}
              </p>
              <p className="text-xs text-muted-foreground mt-1">
                {artistSummary.recouped ? 'Share of profit' : 'After recoupment'}
              </p>
            </Card>
            
            {/* Label Share Card */}
            {isManager && (
              <Card className={`p-4 ${artistSummary.recouped ? 'bg-purple-500/10 border-purple-500/30' : 'bg-muted/30 border-border/50'}`} data-testid="label-share-card">
                <div className={`flex items-center gap-2 mb-1 ${artistSummary.recouped ? 'text-purple-500' : 'text-muted-foreground'}`}>
                  <PoundSterling className="w-4 h-4" />
                  <span className="text-sm font-medium">Label ({100 - (artistSummary.artist_split_percentage || 50)}%)</span>
                </div>
                <p className={`text-2xl font-bold ${artistSummary.recouped ? 'text-purple-500' : 'text-muted-foreground'}`}>
                  {formatCurrency(artistSummary.label_share || 0)}
                </p>
                <p className="text-xs text-muted-foreground mt-1">
                  {artistSummary.recouped ? 'Share of profit' : 'After recoupment'}
                </p>
              </Card>
            )}
          </div>

          {/* Term Info */}
          {artistSummary.term_years && (
            <Card className={`p-4 ${artistSummary.years_remaining <= 2 ? 'bg-orange-500/10 border-orange-500/30' : 'bg-card border-border/50'}`}>
              <div className="flex items-center justify-between">
                <div>
                  <h4 className="font-medium text-sm">Ownership Term</h4>
                  <p className="text-xs text-muted-foreground">
                    {artistSummary.term_years} year deal started {new Date(artistSummary.term_start_date).toLocaleDateString()}
                  </p>
                </div>
                <div className="text-right">
                  <p className={`text-lg font-bold ${artistSummary.years_remaining <= 2 ? 'text-orange-500' : 'text-foreground'}`}>
                    {artistSummary.years_remaining} years remaining
                  </p>
                  <p className="text-xs text-muted-foreground">
                    Expires {new Date(artistSummary.term_end_date).toLocaleDateString()}
                  </p>
                </div>
              </div>
            </Card>
          )}

          {/* Royalty Participants */}
          {artistSummary.royalty_participants && artistSummary.royalty_participants.length > 0 && (
            <Card className="bg-card border-border/50 p-4">
              <h4 className="font-medium text-sm mb-3">Royalty Participants</h4>
              <div className="space-y-2">
                {artistSummary.royalty_participants.map((p) => (
                  <div key={p.id || `${p.name}-${p.role}`} className="flex items-center justify-between text-sm">
                    <div>
                      <span className="font-medium">{p.name}</span>
                      <span className="text-muted-foreground ml-2">({p.role} • {p.percentage}%)</span>
                    </div>
                    <div className="text-right">
                      <span className={artistSummary.recouped ? 'text-green-500' : 'text-muted-foreground'}>
                        {formatCurrency(p.calculated_share || 0)}
                      </span>
                      {p.advance_paid > 0 && (
                        <span className="text-xs text-muted-foreground ml-2">
                          (£{p.advance_paid.toLocaleString()} advance)
                        </span>
                      )}
                    </div>
                  </div>
                ))}
                <div className="border-t border-border pt-2 mt-2 flex justify-between text-sm font-medium">
                  <span>Total to Participants</span>
                  <span>{formatCurrency(artistSummary.royalty_participants.reduce((sum, p) => sum + (p.calculated_share || 0), 0))}</span>
                </div>
              </div>
            </Card>
          )}
        </div>
      )}

      {/* Virgin Royalties Summary Card - Show for managers in all artists view */}
      {isManager && (!selectedArtist || selectedArtist === 'all' || selectedArtist === 'management') && virginRoyalties && virginRoyalties.total_revenue > 0 && (
        <Card 
          className="bg-gradient-to-r from-amber-500/10 to-orange-500/10 border-amber-500/30 p-5 cursor-pointer hover:border-amber-500/50 transition-colors" 
          data-testid="virgin-royalties-summary-card"
          onClick={() => navigate('/royalties')}
        >
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-lg bg-amber-500/20 flex items-center justify-center">
                <Music className="w-5 h-5 text-amber-400" />
              </div>
              <div>
                <h3 className="font-semibold text-lg text-amber-400 flex items-center gap-2">
                  Virgin Royalties
                  <ExternalLink className="w-4 h-4" />
                </h3>
                <p className="text-sm text-muted-foreground">
                  Total paid from DSR statements
                </p>
              </div>
            </div>
            <div className="text-right">
              <p className="text-2xl font-bold text-amber-400">{formatCurrency(virginRoyalties.total_revenue)}</p>
              <p className="text-sm text-muted-foreground">
                {virginRoyalties.total_streams?.toLocaleString()} streams • {virginRoyalties.artists_count} artists • {virginRoyalties.tracks_count} tracks
              </p>
            </div>
          </div>
        </Card>
      )}

      {/* All Artists Summary - Manager view when no artist selected */}
      {isManager && (!selectedArtist || selectedArtist === 'all' || selectedArtist === 'management') && summaries.summaries && summaries.summaries.length > 0 && (
        <Card className="bg-card border-border/50 p-6">
          <div className="flex items-center justify-between mb-4">
            <h2 className="font-semibold text-lg flex items-center gap-2">
              <Music className="w-5 h-5" />
              Label Artist Overview
            </h2>
            {summaries.total_underplay_paythrough > 0 && (
              <div className="text-right">
                <p className="text-xs text-muted-foreground">Total Underplay Paythrough</p>
                <p className="font-bold text-purple-500">{formatCurrency(summaries.total_underplay_paythrough)}</p>
              </div>
            )}
          </div>
          <div className="space-y-3">
            {summaries.summaries.map((summary) => (
              <div 
                key={summary.artist_id}
                className={`flex items-center justify-between p-3 rounded-lg ${
                  summary.recouped 
                    ? 'bg-green-500/5 border border-green-500/20' 
                    : 'bg-red-500/5 border border-red-500/20'
                }`}
              >
                <div className="flex-1">
                  <p className="font-medium">{summary.artist_name}</p>
                  <p className="text-sm text-muted-foreground">
                    {summary.artist_split_percentage}% split
                  </p>
                  <p className={`text-xs ${summary.recouped ? 'text-green-500' : 'text-red-500'}`}>
                    {summary.recouped 
                      ? `✓ Recouped | Balance: ${formatSignedCurrency(summary.balance || 0)}`
                      : `To Recoup: ${formatCurrency(summary.remaining_to_recoup || 0)} | Balance: ${formatSignedCurrency(summary.balance || 0)}`
                    }
                  </p>
                </div>
                <div className="flex items-center gap-4">
                  <div className="text-right">
                    <p className={`font-bold ${summary.recouped ? 'text-green-500' : 'text-orange-500'}`}>
                      Artist: {formatCurrency(summary.artist_share)}
                    </p>
                    <p className={`text-xs ${summary.recouped ? 'text-green-500' : 'text-red-500'}`}>
                      {summary.recouped ? '✓ In Profit' : 'Unrecouped'}
                    </p>
                  </div>
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={() => openDealSettings(labelArtists.find(a => a.id === summary.artist_id) || { id: summary.artist_id, name: summary.artist_name, ...summary })}
                    className="text-muted-foreground hover:text-foreground"
                  >
                    <Settings className="w-4 h-4" />
                  </Button>
                </div>
              </div>
            ))}
          </div>
        </Card>
      )}

      {/* No label artists message */}
      {isManager && labelArtists.length === 0 && (
        <Card className="bg-card border-border/50 p-8 text-center">
          <Music className="w-12 h-12 mx-auto text-muted-foreground mb-4" />
          <h3 className="text-lg font-semibold mb-2">No Label Artists</h3>
          <p className="text-muted-foreground mb-4">
            To get started, enable &quot;Label Artist&quot; in an artist&apos;s settings.
          </p>
          <p className="text-sm text-muted-foreground">
            Go to Artists → Edit → Toggle &quot;Label Artist&quot; ON
          </p>
        </Card>
      )}

      {/* Transactions List */}
      {transactions.length > 0 && (
        <Card className="bg-card border-border/50 p-6">
          <div className="flex items-center justify-between mb-4">
            <h2 className="font-semibold text-lg flex items-center gap-2">
              <FileText className="w-5 h-5" />
              {viewMode === 'itemized' ? 'Transaction History' : 'Spend by Category'}
              {displayCurrency !== 'GBP' && (
                <span className="text-xs text-muted-foreground ml-2">
                  (showing in {displayCurrency})
                </span>
              )}
            </h2>
            {/* View Mode Toggle */}
            <div className="flex gap-1 bg-muted/30 rounded-lg p-1">
              <Button
                variant={viewMode === 'itemized' ? 'default' : 'ghost'}
                size="sm"
                onClick={() => setViewMode('itemized')}
                className="text-xs"
                data-testid="view-itemized-btn"
              >
                Itemized
              </Button>
              <Button
                variant={viewMode === 'category' ? 'default' : 'ghost'}
                size="sm"
                onClick={() => setViewMode('category')}
                className="text-xs"
                data-testid="view-category-btn"
              >
                By Category
              </Button>
            </div>
          </div>

          {/* Category Summary View */}
          {viewMode === 'category' && (
            <div className="space-y-2">
              {getCategoryTotals().map((cat) => (
                <div 
                  key={cat.category}
                  className="flex items-center justify-between p-3 bg-muted/30 rounded-lg"
                >
                  <div className="flex items-center gap-3">
                    <div className="w-8 h-8 rounded-full bg-primary/20 flex items-center justify-center">
                      <span className="text-xs font-bold text-primary">{cat.count}</span>
                    </div>
                    <div>
                      <p className="font-medium">{cat.category}</p>
                      <p className="text-sm text-muted-foreground">
                        {cat.count} transaction{cat.count !== 1 ? 's' : ''}
                      </p>
                    </div>
                  </div>
                  <div className="text-right">
                    {cat.costs > 0 && (
                      <p className="font-bold text-red-500">
                        -{formatCurrency(convertAmount(cat.costs), displayCurrency)}
                      </p>
                    )}
                    {cat.income > 0 && (
                      <p className="font-bold text-green-500">
                        +{formatCurrency(convertAmount(cat.income), displayCurrency)}
                      </p>
                    )}
                    {displayCurrency !== 'GBP' && (
                      <p className="text-xs text-muted-foreground">
                        {cat.costs > 0 ? `-£${cat.costs.toFixed(2)}` : ''}
                        {cat.income > 0 ? ` +£${cat.income.toFixed(2)}` : ''}
                      </p>
                    )}
                  </div>
                </div>
              ))}
              
              {/* Category Totals */}
              <div className="mt-4 pt-4 border-t border-border/50">
                <div className="flex justify-between items-center">
                  <span className="font-semibold">Total Costs:</span>
                  <span className="font-bold text-red-500">
                    -{formatCurrency(convertAmount(getCategoryTotals().reduce((sum, c) => sum + c.costs, 0)), displayCurrency)}
                  </span>
                </div>
                <div className="flex justify-between items-center mt-1">
                  <span className="font-semibold">Total Income:</span>
                  <span className="font-bold text-green-500">
                    +{formatCurrency(convertAmount(getCategoryTotals().reduce((sum, c) => sum + c.income, 0)), displayCurrency)}
                  </span>
                </div>
              </div>
            </div>
          )}

          {/* Itemized View */}
          {viewMode === 'itemized' && (
          <div className="space-y-2">
            {transactions.map((transaction) => (
              <div 
                key={transaction.id}
                className="flex items-center justify-between p-3 bg-muted/30 rounded-lg group"
              >
                <div className="flex items-center gap-3">
                  {transaction.transaction_type === 'cost' ? (
                    <div className="w-8 h-8 rounded-full bg-red-500/20 flex items-center justify-center">
                      <ArrowDownCircle className="w-4 h-4 text-red-500" />
                    </div>
                  ) : (
                    <div className="w-8 h-8 rounded-full bg-green-500/20 flex items-center justify-center">
                      <ArrowUpCircle className="w-4 h-4 text-green-500" />
                    </div>
                  )}
                  <div>
                    <p className="font-medium">{transaction.description}</p>
                    <p className="text-sm text-muted-foreground">
                      {getArtistName(transaction.artist_id)} • {transaction.category} • {new Date(transaction.date).toLocaleDateString('en-GB')}
                    </p>
                  </div>
                </div>
                <div className="flex items-center gap-3">
                  <div className="text-right">
                    <p className={`font-bold ${transaction.transaction_type === 'income' ? 'text-green-500' : 'text-red-500'}`}>
                      {transaction.transaction_type === 'income' ? '+' : '-'}{formatCurrency(convertAmount(transaction.amount), displayCurrency)}
                    </p>
                    {displayCurrency !== 'GBP' && (
                      <p className="text-xs text-muted-foreground">
                        £{Math.abs(transaction.amount).toFixed(2)}
                      </p>
                    )}
                  </div>
                  {isManager && (
                    <>
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => handleEdit(transaction)}
                        className="text-muted-foreground hover:text-foreground hover:bg-muted"
                      >
                        <Pencil className="w-4 h-4" />
                      </Button>
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => confirmDelete(transaction)}
                        className="text-red-500 hover:text-red-600 hover:bg-red-500/10"
                      >
                        <Trash2 className="w-4 h-4" />
                      </Button>
                    </>
                  )}
                </div>
              </div>
            ))}
          </div>
          )}
        </Card>
      )}

      {/* No transactions */}
      {transactions.length === 0 && labelArtists.length > 0 && (
        <Card className="bg-card border-border/50 p-8 text-center">
          <FileText className="w-12 h-12 mx-auto text-muted-foreground mb-4" />
          <h3 className="text-lg font-semibold mb-2">No Transactions Yet</h3>
          <p className="text-muted-foreground">
            {isManager 
              ? 'Add your first cost or income to start tracking.'
              : 'No transactions have been recorded yet.'}
          </p>
        </Card>
      )}

      {/* Delete Confirmation Dialog */}
      <DeleteConfirmDialog
        open={deleteDialogOpen}
        onOpenChange={(open) => {
          setDeleteDialogOpen(open);
          if (!open) setTransactionToDelete(null);
        }}
        onConfirm={handleDelete}
        title="Delete Transaction"
        itemName={transactionToDelete?.description}
        description="Are you sure you want to delete this transaction? This action cannot be undone."
      />

      {/* Deal Settings Modal */}
      <Dialog open={showDealSettings} onOpenChange={setShowDealSettings}>
        <DialogContent className="max-w-2xl max-h-[85vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="font-manrope font-bold text-xl">
              Deal Settings - {selectedArtistForSettings?.name}
            </DialogTitle>
            <p className="text-sm text-muted-foreground">
              Configure the deal terms and royalty participants for this artist
            </p>
          </DialogHeader>
          
          <div className="space-y-6">
            {/* Financial Terms */}
            <div className="space-y-4">
              <h3 className="font-semibold text-sm border-b border-border pb-2">Financial Terms</h3>
              
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label>Distributor Fee</Label>
                  <div className="flex items-center gap-2">
                    <Input
                      type="number"
                      step="0.1"
                      min="0"
                      max="100"
                      value={dealSettings.distributor_fee_percentage}
                      onChange={(e) => setDealSettings({ ...dealSettings, distributor_fee_percentage: parseFloat(e.target.value) || 0 })}
                      className="bg-input/50 border-border rounded-xl"
                    />
                    <span className="text-muted-foreground">%</span>
                  </div>
                  <p className="text-xs text-muted-foreground">Fee taken off top by distributor</p>
                </div>

                <div className="space-y-2">
                  <Label>Underplay Paythrough</Label>
                  <div className="flex items-center gap-2">
                    <Input
                      type="number"
                      step="0.1"
                      min="0"
                      max="100"
                      value={dealSettings.underplay_paythrough_percentage}
                      onChange={(e) => setDealSettings({ ...dealSettings, underplay_paythrough_percentage: parseFloat(e.target.value) || 0 })}
                      className="bg-input/50 border-border rounded-xl"
                    />
                    <span className="text-muted-foreground">%</span>
                  </div>
                  <p className="text-xs text-muted-foreground">Hidden from artist</p>
                </div>
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label>Artist Split</Label>
                  <div className="flex items-center gap-2">
                    <Input
                      type="number"
                      step="0.1"
                      min="0"
                      max="100"
                      value={dealSettings.artist_split_percentage}
                      onChange={(e) => setDealSettings({ ...dealSettings, artist_split_percentage: parseFloat(e.target.value) || 0 })}
                      className="bg-input/50 border-border rounded-xl"
                    />
                    <span className="text-muted-foreground">%</span>
                  </div>
                  <p className="text-xs text-muted-foreground">Label gets {100 - (dealSettings.artist_split_percentage || 0)}%</p>
                </div>

                <div className="space-y-2">
                  <Label>Cost Recoupment Model</Label>
                  <select
                    value={dealSettings.cost_recoupment_model}
                    onChange={(e) => setDealSettings({ ...dealSettings, cost_recoupment_model: e.target.value })}
                    className="w-full px-3 py-2 bg-input/50 border border-border text-foreground rounded-xl focus:border-primary outline-none text-sm"
                  >
                    <option value="costs_off_top">Costs Off The Top</option>
                    <option value="costs_from_artist">Costs From Artist Share</option>
                  </select>
                  <p className="text-xs text-muted-foreground">
                    {dealSettings.cost_recoupment_model === 'costs_off_top' 
                      ? 'Costs are deducted before splitting profit (standard)' 
                      : 'Costs are deducted only from artist\'s share'}
                  </p>
                </div>
              </div>
            </div>

            {/* Term/Ownership */}
            <div className="space-y-4">
              <h3 className="font-semibold text-sm border-b border-border pb-2">Term &amp; Ownership</h3>
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label>Term Length (Years)</Label>
                  <Input
                    type="number"
                    min="1"
                    max="50"
                    value={dealSettings.term_years}
                    onChange={(e) => setDealSettings({ ...dealSettings, term_years: parseInt(e.target.value) || '' })}
                    placeholder="e.g., 10"
                    className="bg-input/50 border-border rounded-xl"
                  />
                  <p className="text-xs text-muted-foreground">How long Underplay owns the recordings</p>
                </div>

                <div className="space-y-2">
                  <Label>Term Start Date</Label>
                  <Input
                    type="date"
                    value={dealSettings.term_start_date}
                    onChange={(e) => setDealSettings({ ...dealSettings, term_start_date: e.target.value })}
                    className="bg-input/50 border-border rounded-xl"
                  />
                  <p className="text-xs text-muted-foreground">When the term began</p>
                </div>
              </div>
            </div>

            {/* Royalty Participants */}
            <div className="space-y-4">
              <div className="flex items-center justify-between border-b border-border pb-2">
                <h3 className="font-semibold text-sm">Royalty Participants</h3>
                <Button
                  size="sm"
                  variant="outline"
                  onClick={() => setShowParticipantDialog(true)}
                  className="h-7 text-xs"
                >
                  <Plus className="w-3 h-3 mr-1" /> Add
                </Button>
              </div>
              <p className="text-xs text-muted-foreground">
                Producers, mixers, or collaborators with royalty percentages (paid from artist&apos;s share after recoupment)
              </p>
              
              {royaltyParticipants.length === 0 ? (
                <p className="text-sm text-muted-foreground italic">No royalty participants added</p>
              ) : (
                <div className="space-y-2">
                  {royaltyParticipants.map((p) => (
                    <div key={p.id} className="flex items-center justify-between p-2 bg-muted/30 rounded-lg">
                      <div>
                        <p className="font-medium text-sm">{p.name}</p>
                        <p className="text-xs text-muted-foreground">
                          {p.role} • {p.percentage}% royalty
                          {p.advance_paid > 0 && ` • £${p.advance_paid.toLocaleString()} advance`}
                        </p>
                      </div>
                      <Button
                        size="sm"
                        variant="ghost"
                        onClick={() => deleteRoyaltyParticipant(p.id)}
                        className="h-7 w-7 p-0 text-red-500 hover:text-red-600"
                      >
                        <Trash2 className="w-3 h-3" />
                      </Button>
                    </div>
                  ))}
                  <div className="text-xs text-muted-foreground pt-1">
                    Total: {royaltyParticipants.reduce((sum, p) => sum + p.percentage, 0)}% of artist royalties
                  </div>
                </div>
              )}
            </div>

            <div className="flex gap-2 pt-4 border-t border-border">
              <Button variant="outline" onClick={() => setShowDealSettings(false)} className="flex-1">
                Cancel
              </Button>
              <Button onClick={saveDealSettings} className="flex-1">
                Save Settings
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* Add Royalty Participant Dialog */}
      <Dialog open={showParticipantDialog} onOpenChange={setShowParticipantDialog}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>Add Royalty Participant</DialogTitle>
            <p className="text-sm text-muted-foreground">
              Add a producer, mixer, or collaborator with a royalty percentage
            </p>
          </DialogHeader>
          <form onSubmit={addRoyaltyParticipant} className="space-y-4">
            <div className="space-y-2">
              <Label>Name</Label>
              <Input
                value={participantForm.name}
                onChange={(e) => setParticipantForm({ ...participantForm, name: e.target.value })}
                placeholder="e.g., John Smith"
                className="bg-input/50 border-border rounded-xl"
                required
              />
            </div>
            
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>Role</Label>
                <select
                  value={participantForm.role}
                  onChange={(e) => setParticipantForm({ ...participantForm, role: e.target.value })}
                  className="w-full px-3 py-2 bg-input/50 border border-border text-foreground rounded-xl focus:border-primary outline-none"
                >
                  {participantRoles.map((role) => (
                    <option key={role} value={role}>{role}</option>
                  ))}
                </select>
              </div>
              
              <div className="space-y-2">
                <Label>Royalty %</Label>
                <Input
                  type="number"
                  step="0.1"
                  min="0"
                  max="100"
                  value={participantForm.percentage}
                  onChange={(e) => setParticipantForm({ ...participantForm, percentage: e.target.value })}
                  placeholder="e.g., 4"
                  className="bg-input/50 border-border rounded-xl"
                  required
                />
              </div>
            </div>

            <div className="space-y-2">
              <Label>Advance Paid (£)</Label>
              <Input
                type="number"
                step="0.01"
                min="0"
                value={participantForm.advance_paid}
                onChange={(e) => setParticipantForm({ ...participantForm, advance_paid: e.target.value })}
                placeholder="0.00"
                className="bg-input/50 border-border rounded-xl"
              />
              <p className="text-xs text-muted-foreground">Any advance already paid to this participant</p>
            </div>

            <div className="space-y-2">
              <Label>Notes (Optional)</Label>
              <Textarea
                value={participantForm.notes}
                onChange={(e) => setParticipantForm({ ...participantForm, notes: e.target.value })}
                placeholder="Any additional details..."
                className="bg-input/50 border-border rounded-xl"
                rows={2}
              />
            </div>

            <div className="flex gap-2 pt-2">
              <Button type="button" variant="outline" onClick={() => setShowParticipantDialog(false)} className="flex-1">
                Cancel
              </Button>
              <Button type="submit" className="flex-1">
                Add Participant
              </Button>
            </div>
          </form>
        </DialogContent>
      </Dialog>

      {/* Royalty Payout Dialog */}
      <Dialog open={payoutDialogOpen} onOpenChange={setPayoutDialogOpen}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Banknote className="w-5 h-5 text-green-400" />
              Record Royalty Payout
            </DialogTitle>
          </DialogHeader>
          <form onSubmit={handleRecordPayout} className="space-y-4">
            <div>
              <Label className="text-sm font-medium">Payout Amount (£)</Label>
              <Input
                type="number"
                step="0.01"
                min="0"
                value={payoutForm.amount}
                onChange={(e) => setPayoutForm({...payoutForm, amount: e.target.value})}
                placeholder="Enter amount paid to artist"
                className="bg-input/50 border-border mt-1"
                required
              />
              {artistRoyalties && (
                <p className="text-xs text-muted-foreground mt-1">
                  Available royalty balance: {formatCurrency(artistRoyalties.total_revenue)}
                </p>
              )}
            </div>
            <div>
              <Label className="text-sm font-medium">Date</Label>
              <Input
                type="date"
                value={payoutForm.date}
                onChange={(e) => setPayoutForm({...payoutForm, date: e.target.value})}
                className="bg-input/50 border-border mt-1"
                required
              />
            </div>
            <div>
              <Label className="text-sm font-medium">Notes (optional)</Label>
              <Textarea
                value={payoutForm.notes}
                onChange={(e) => setPayoutForm({...payoutForm, notes: e.target.value})}
                placeholder="Payment reference, method, etc."
                className="bg-input/50 border-border mt-1"
                rows={2}
              />
            </div>
            <div className="flex gap-2 pt-2">
              <Button type="button" variant="outline" onClick={() => setPayoutDialogOpen(false)} className="flex-1">
                Cancel
              </Button>
              <Button type="submit" className="flex-1 bg-green-600 hover:bg-green-700">
                Record Payout
              </Button>
            </div>
          </form>
        </DialogContent>
      </Dialog>

      {/* Import Royalty as Income Dialog */}
      <Dialog open={importRoyaltyDialogOpen} onOpenChange={setImportRoyaltyDialogOpen}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Import className="w-5 h-5 text-amber-400" />
              Import Royalty as Income
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <p className="text-sm text-muted-foreground">
              This will create an income transaction for the artist's royalty amount, which will count towards recoupment.
            </p>
            
            {artistRoyalties && (
              <div className="bg-amber-500/10 border border-amber-500/30 rounded-lg p-4">
                <div className="flex justify-between items-center mb-2">
                  <span className="text-sm font-medium">Royalty Amount</span>
                  <span className="font-bold text-amber-400">{formatCurrency(artistRoyalties.total_revenue)}</span>
                </div>
                <div className="text-xs text-muted-foreground space-y-1">
                  <p>• {artistRoyalties.total_streams?.toLocaleString() || 0} streams</p>
                  <p>• {artistRoyalties.track_count || 0} tracks</p>
                  <p>• Will be recorded as "Streaming" income</p>
                </div>
              </div>
            )}
            
            <div className="flex gap-2 pt-2">
              <Button variant="outline" onClick={() => setImportRoyaltyDialogOpen(false)} className="flex-1">
                Cancel
              </Button>
              <Button onClick={handleImportRoyalty} className="flex-1 bg-amber-600 hover:bg-amber-700">
                Import as Income
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default RecordLabel;
